import { useState } from 'react';
import { useReprise } from '@/hooks/useInterventionMutations';
import { useMechanics } from '@/hooks/useInterventions';
import { useToast } from '@/hooks/useToast';
import { HttpError } from '@/lib/api';
import { todayIso } from '@/lib/dates';
import type { Intervention } from '@/lib/types';

export default function RepriseDialog({
  intervention,
  onClose,
}: {
  intervention: Intervention;
  onClose: () => void;
}) {
  const reprise = useReprise();
  const toast = useToast();
  const mechanicsQuery = useMechanics();

  const mecanos = (mechanicsQuery.data ?? [])
    .filter((m) => m.actif)
    .sort((a, b) => a.ordreAffichage - b.ordreAffichage);

  const [mode, setMode] = useState<'choice' | 'schedule'>('choice');
  const [mecanicienId, setMecanicienId] = useState(intervention.mecanicienId);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');
  const [duree, setDuree] = useState(intervention.dureeEstimeeMinutes);

  function runNow() {
    reprise.mutate(
      { id: intervention.id, mode: 'now' },
      {
        onSuccess: () => {
          toast.success('Intervention reprise');
          onClose();
        },
        onError: (err) => {
          toast.error(err instanceof HttpError ? err.payload.message : 'Erreur de reprise');
        },
      },
    );
  }

  function runSchedule() {
    if (!date) {
      toast.error('Veuillez choisir une date');
      return;
    }
    reprise.mutate(
      {
        id: intervention.id,
        mode: 'schedule',
        mecanicienId,
        creneauDebut: `${date}T${time}:00.000Z`, // heure-mur = UTC
        dureeEstimeeMinutes: duree,
      },
      {
        onSuccess: () => {
          toast.success('RDV replanifié');
          onClose();
        },
        onError: (err) => {
          toast.error(err instanceof HttpError ? err.payload.message : 'Erreur de reprise');
        },
      },
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/30 flex items-center justify-center px-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-surface-white border border-border-light rounded-lg shadow-lg p-5 w-96 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-text-primary">
          La pièce est arrivée — reprendre
        </h2>

        {mode === 'choice' && (
          <div className="space-y-2">
            <button
              type="button"
              className="btn btn-primary w-full"
              disabled={reprise.isPending}
              onClick={runNow}
            >
              Maintenant (reprendre tout de suite)
            </button>
            <button
              type="button"
              className="btn btn-secondary w-full"
              onClick={() => setMode('schedule')}
            >
              Planifier un créneau
            </button>
            <button
              type="button"
              className="btn btn-secondary w-full"
              onClick={onClose}
            >
              Annuler
            </button>
          </div>
        )}

        {mode === 'schedule' && (
          <div className="space-y-3">
            <label className="block space-y-1">
              <span className="input-label">Mécanicien</span>
              <select
                className="input"
                value={mecanicienId}
                onChange={(e) => setMecanicienId(e.target.value)}
              >
                {mecanos.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.prenom} {m.nom}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block space-y-1">
                <span className="input-label">Date RDV</span>
                <input
                  type="date"
                  className="input"
                  value={date}
                  min={todayIso()}
                  onChange={(e) => setDate(e.target.value)}
                />
              </label>
              <label className="block space-y-1">
                <span className="input-label">Heure début</span>
                <input
                  type="time"
                  step={900}
                  className="input"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </label>
            </div>
            <label className="block space-y-1">
              <span className="input-label">Durée (min)</span>
              <input
                type="number"
                min={15}
                step={15}
                className="input w-32"
                value={duree}
                onChange={(e) => setDuree(Number(e.target.value))}
              />
            </label>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                className="btn btn-secondary flex-1"
                onClick={() => setMode('choice')}
              >
                Retour
              </button>
              <button
                type="button"
                className="btn btn-primary flex-1"
                disabled={reprise.isPending}
                onClick={runSchedule}
              >
                Replanifier
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
